// src/products/capRaise/pages/QuestionsIntegratePage.js
// Questions integration page - shows all CapRaise questions with filtering

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import simpleQuestionsApi from '../../questions/services/simpleQuestionsApi';

const QuestionsIntegratePage = ({ 
  user, 
  selectedCompany, 
  activeRound, 
  addMessage 
}) => {
  const navigate = useNavigate();
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Load all CapRaise questions
  useEffect(() => {
    const loadAllQuestions = async () => {
      setLoading(true);
      try {
        const response = await simpleQuestionsApi.getQuestions({
          module_type: 'capraise'
        });
        
        if (response.success) {
          setAllQuestions(response.data || []);
          setFilteredQuestions(response.data || []);
        }
      } catch (error) {
        console.error('Error loading questions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllQuestions();
  }, []);

  // Filter questions based on selected round and search
  useEffect(() => {
    let filtered = allQuestions;

    // Filter by selected round if one is selected
    if (activeRound?.id) {
      filtered = filtered.filter(q => q.module_reference_id === activeRound.id);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }

    setFilteredQuestions(filtered);
  }, [allQuestions, activeRound?.id, searchTerm, statusFilter]);

  // Status configuration (matching SimpleQuestionsPage)
  const STATUS_CONFIG = {
    open: { 
      bg: 'bg-orange-50 border-orange-200', 
      text: 'text-orange-800', 
      label: 'Open',
      icon: Clock
    },
    answered: { 
      bg: 'bg-green-50 border-green-200', 
      text: 'text-green-800', 
      label: 'Answered',
      icon: CheckCircle
    },
    duplicate: { 
      bg: 'bg-purple-50 border-purple-200', 
      text: 'text-purple-800', 
      label: 'Duplicate',
      icon: AlertCircle
    },
    out_of_scope: { 
      bg: 'bg-gray-50 border-gray-200', 
      text: 'text-gray-800', 
      label: 'Out of Scope',
      icon: AlertCircle
    },
    closed: { 
      bg: 'bg-blue-50 border-blue-200', 
      text: 'text-blue-800', 
      label: 'Closed',
      icon: CheckCircle
    }
  };

  // Get status configuration
  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.open;
  };

  // Get priority color (matching SimpleQuestionsPage)
  const getPriorityColor = (priority) => {
    if (priority === 'high') {
      return 'bg-red-50 text-red-800 border-red-200';
    }
    return 'bg-gray-50 text-gray-800 border-gray-200';
  };

  // Get context color (matching SimpleQuestionsPage)
  const getContextColor = (moduleType) => {
    switch (moduleType) {
      case 'dataroom':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'capraise':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'equity_manager':
        return 'bg-purple-50 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  // Format time ago (matching SimpleQuestionsPage)
  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Handle question click to navigate to detail
  const handleQuestionClick = (questionId) => {
    navigate(`/questions/detail/${questionId}`);
  };

  if (!activeRound) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="h-16 w-16 text-gray-400 mx-auto mb-4">🎯</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Round Selected</h3>
          <p className="text-gray-500 mb-4">Please select an active capital raising round to view questions.</p>
          <button
            onClick={() => navigate('/capraise/round-builder')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Round Builder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Questions</h1>
            <p className="text-gray-600 mt-1">
              {activeRound 
                ? `Questions for ${activeRound.round_name}` 
                : 'All CapRaise Questions'
              }
            </p>
            {activeRound && (
              <p className="text-sm text-blue-600 mt-1">
                Showing questions for selected round. Use the round dropdown above to switch rounds.
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/capraise')}
            className="inline-flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to CapRaise Dashboard
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="duplicate">Duplicate</option>
            <option value="out_of_scope">Out of Scope</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-lg border border-gray-200 max-h-[calc(100vh-300px)] overflow-y-auto">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading questions...</p>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-12">
            <div className="h-12 w-12 text-gray-400 mx-auto mb-4">💬</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' || activeRound
                ? 'No questions match your current filters' 
                : 'No questions have been asked yet'
              }
            </p>
            {(!searchTerm && statusFilter === 'all' && !activeRound) && (
              <button
                onClick={() => navigate('/capraise')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to a Round to Ask Questions
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredQuestions.map((question) => (
              <div 
                key={question.id} 
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleQuestionClick(question.id)}
              >
                {/* Metadata Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {/* Time */}
                    <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      {getTimeSince(question.created_at)}
                    </span>
                    
                    {/* Priority */}
                    <span className={`text-xs px-2 py-1 rounded border ${getPriorityColor(question.priority)}`}>
                      {question.priority === 'high' ? 'High Priority' : 'Normal'}
                    </span>
                    
                    {/* Status */}
                    {(() => {
                      const statusConfig = getStatusConfig(question.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${statusConfig.bg} ${statusConfig.text}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </span>
                      );
                    })()}
                    
                    {/* Context */}
                    <span className={`text-xs px-2 py-1 rounded border ${getContextColor(question.module_type)}`}>
                      CapRaise
                    </span>
                  </div>
                </div>

                {/* Question Content */}
                <div className="pl-0">
                  <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1">
                    {question.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {question.description}
                  </p>
                  
                  {/* Footer Info */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-3">
                      <span>{question.user_name || 'Unknown'}</span>
                      <span>•</span>
                      <span>{question.category}</span>
                      <span>•</span>
                      <span>Round: {question.module_reference_name}</span>
                    </div>
                    
                    {/* Response Count */}
                    {question.responses && question.responses.length > 0 && (
                      <span className="text-blue-600 font-medium">
                        {question.responses.length} response{question.responses.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionsIntegratePage;